/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useMemo } from 'react';
import { View, TextStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import PressableView from '@airasia-phoenix/react-native/components/PressableView';
import type { PressableRenderState } from '@airasia-phoenix/react-native/components/PressableView';
import {
  pd,
  getTextStyle,
  getColor,
  getShape,
} from '@airasia-phoenix/react-native/utils/utils';

export interface CurrencyRowProps {
  primaryText: string;
  primaryTextStyle?: TextStyle;
  onPress?: () => void;
  isSelected?: boolean;
  testID?: string;
}

const CurrencyRow = ({
  primaryText,
  primaryTextStyle,
  onPress,
  isSelected = false,
  testID,
}: CurrencyRowProps) => {
  const { codeText, nameText } = useMemo(() => {
    if (!primaryText) {
      return { codeText: '', nameText: '' };
    }
    const [code, ...rest] = primaryText.split(' - ');
    return {
      codeText: code ?? '',
      nameText: rest.length ? ` - ${rest.join(' - ')}` : '',
    };
  }, [primaryText]);

  const getRowContainerStyle = useCallback(
    ({ isHovered, isPointerDown }: PressableRenderState) => [
      styles.container,
      (isHovered || isSelected || isPointerDown) &&
        getShape(pd.sys.shape.corner.small),
      isSelected && {
        boxShadow: [
          {
            inset: true,
            offsetX: 0,
            offsetY: 0,
            spreadDistance: 2,
            color: getColor(pd.sys.colour.secondary),
          },
        ],
      },
      {
        backgroundColor: isPointerDown
          ? pd.sys.colour.state_pressed.toString()
          : isHovered || isSelected
            ? getColor(pd.sys.colour.secondary_low)
            : getColor(pd.comp.list.container.colour),
      },
    ],
    [isSelected],
  );

  return (
    <PressableView
      {...(testID ? { testID } : {})}
      onPress={onPress}
      style={getRowContainerStyle}
    >
      <View style={styles.textView}>
        <View style={styles.textRow}>
          {!!codeText && (
            <TextView
              text={codeText}
              numberOfLines={1}
              style={[
                primaryTextStyle,
                getTextStyle(
                  pd.sys.typescale.body_text_medium,
                  pd.comp.list.primaryText.colour,
                ),
              ]}
              testID="currency-code-text"
            />
          )}
          {!!nameText && (
            <TextView
              text={nameText}
              numberOfLines={1}
              style={[
                getTextStyle(
                  pd.comp.list.primaryText.type,
                  pd.comp.list.primaryText.colour,
                ),
                primaryTextStyle,
              ]}
              testID="currency-name-text"
            />
          )}
        </View>
      </View>

      {isSelected && (
        <View style={styles.iconView}>
          <IconView
            height={24}
            width={24}
            tint={getColor(pd.sys.colour.secondary)}
            icon={pd.icon.main.navigation.check}
          />
        </View>
      )}
    </PressableView>
  );
};
const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    paddingHorizontal: pd.ref.space.spacing40.px,
    paddingVertical: pd.ref.space.spacing20.px,
  },
  textView: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    paddingRight: pd.ref.space.spacing40.px,
  },
  textRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconView: {
    flexShrink: 0,
    marginLeft: pd.ref.space.spacing20.px,
  },
});
export default CurrencyRow;
